import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import { Auth } from './components/Auth';
import Index from './pages/Index';
import Privacy from './pages/Privacy';
import Imprint from './pages/Imprint';
import Terms from './pages/Terms';
import Demo from './pages/Demo';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CreateBlogPost from './pages/CreateBlogPost';
import SavedArticles from './pages/SavedArticles';
import Institutionen from './pages/Institutionen';
import Success from './pages/Success';
import { AuthModal } from './components/AuthModal';
import { useState, useEffect } from 'react';

// Komponente, die automatisch beim Routenwechsel zum Seitenanfang scrollt
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, retryAuth } = useAuth();
  const [showRetry, setShowRetry] = useState(false);

  // Zeige Retry-Button nach 10 Sekunden
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setShowRetry(true);
      }, 10000);
      
      return () => clearTimeout(timer);
    } else {
      setShowRetry(false);
    }
  }, [loading]);

  console.log('🛡️ ProtectedRoute - User:', user?.email || 'None', 'Loading:', loading);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50">
        <div className="text-center">
          <div className="relative">
            {/* Spinning Circle */}
            <div className="w-16 h-16 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
            {/* Pulsing Dot */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-600 rounded-full animate-pulse"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Anmeldung wird überprüft...</h3>
          <p className="text-gray-600 text-sm mb-4">Einen Moment bitte</p>
          
          {showRetry && (
            <button
              onClick={() => {
                setShowRetry(false);
                retryAuth();
              }}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Erneut versuchen
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('🚫 No user found, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  console.log('✅ User authenticated, rendering protected content');
  return <>{children}</>;
};

export function AppRoutes() {
  const { isAuthModalOpen, hideAuthModal, authModalTab } = useAuth();
  const location = useLocation();

  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/success" element={<Success />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/imprint" element={<Imprint />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/blog/create" element={<CreateBlogPost />} />
      <Route path="/blog/saved" element={<SavedArticles />} />
      <Route path="/institutionen" element={<Institutionen />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
      {location.pathname !== '/auth' && (
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={hideAuthModal}
          defaultTab={authModalTab}
        />
      )}
    </>
  );
} 